import { useState } from 'react';
import { api } from '../api.js';
import { isQuarantineTank, testCardsPatch } from '../../../shared/tank-purpose.js';

const hideKey = (id) => `aqs.cycleCardsNote.${id}`;

function readHidden(id) {
  try {
    return localStorage.getItem(hideKey(id)) === '1';
  } catch {
    return false;
  }
}

// A tank cycling without ammonia and nitrite cards cannot log its cycle:
// the Log page and the server only take a parameter the tank has a card
// for (feedback #61). One tap adds the standard cards. A card the keeper
// hid stays hidden; only a missing one is offered, and "Not now" is
// remembered on this device, for a keeper who seeded the tank or removed
// the cards on purpose.
export function CycleCardsNote({ tank, onReload }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [hidden, setHidden] = useState(() => readHidden(tank?.id));
  if (!tank || hidden || tank.role !== 'owner' || isQuarantineTank(tank)) return null;
  if (tank.phase !== 'cycling' && tank.phase !== 'setup') return null;
  // An empty params blob falls back to the baseline cards on screen; one
  // saved card would replace them all.
  if (!tank.params || !Object.keys(tank.params).length) return null;
  const patch = testCardsPatch(tank.params);
  const missing = Object.keys(patch);
  if (!missing.length) return null;
  const cards = missing.length > 1 ? 'cards' : 'a card';
  const names = missing.map((k) => patch[k].label.toLowerCase()).join(' and ');
  const lead = tank.phase === 'setup' ? 'Your new tank will cycle.' : 'Your tank is cycling.';

  const add = async () => {
    setBusy(true);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/params`, { method: 'PATCH', body: { params: patch } });
      await onReload?.();
    } catch (e) {
      setErr(e.message || 'Could not add them.');
    } finally {
      setBusy(false);
    }
  };

  const notNow = () => {
    try {
      localStorage.setItem(hideKey(tank.id), '1');
    } catch {
      // Private mode: it hides for this visit only.
    }
    setHidden(true);
  };

  return (
    <div
      style={{
        border: '1px solid var(--accent-border)',
        background: 'var(--accent-bg)',
        borderRadius: 12,
        padding: 12,
        marginBottom: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <p style={{ margin: 0, fontSize: 15, lineHeight: 1.45, color: 'var(--text)' }}>
        {lead} Add {cards} for {names} so you can log the cycle. While no fish, coral or invert is
        listed in this tank they are charted, not alerted. Fish already in? Add them under Livestock
        so ammonia alerts you.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <button
          type="button"
          className="ts-chip"
          disabled={busy}
          style={{ minHeight: 44, background: 'var(--cyan)', color: 'var(--cyan-text)' }}
          onClick={add}
        >
          {busy ? 'Adding…' : `Add ${names} ${missing.length > 1 ? 'cards' : 'card'}`}
        </button>
        <button
          type="button"
          className="ts-chip"
          disabled={busy}
          style={{ minHeight: 44 }}
          onClick={notNow}
        >
          Not now
        </button>
      </div>
      {err && (
        <div role="alert" className="ts-err">
          {err}
        </div>
      )}
    </div>
  );
}
